import { useContext, useState } from "react";
import { TranslationService } from "../../services/TranslationService";
import { InboxContext, QuickFilterType } from "./InboxTableProvider";
import CounterMsgContext from "../shared/CounterMsgContext";
import TooltipComponent from "../shared/TooltipComponent";
import CompanyService from "../../services/CompanyService";
import spamIconSrc from "../../assets/img/hexagon-exclamation.svg";

interface InboxSidebarProps {
  onFolderChange: (folder: string) => void;
  selectedFolder: string;
}

export const InboxSidebar = ({
  onFolderChange,
  selectedFolder
}: InboxSidebarProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { request, setRequest } = useContext(InboxContext);
  const { inboxCount } = useContext(CounterMsgContext);
  const { translate } = TranslationService;

  const isEmailSelected = request.quickfilter === QuickFilterType.Email;
  const isWhatsAppSelected =
    request.quickfilter === QuickFilterType.Whatsapp ||
    request.quickfilter === QuickFilterType.WhatsappInbound;

  const handleEmailClick = () => {
    setRequest((prev) => ({
      ...prev,
      quickfilter: QuickFilterType.Email,
      spam: false,
      read: undefined,
      archived: false
    }));
    onFolderChange("received");
  };

  const handleWhatsAppClick = () => {
    setRequest((prev) => {
      return {
        ...prev,
        quickfilter: QuickFilterType.Whatsapp,
        spam: false,
        read: false,
        archived: false
      };
    });
    onFolderChange("received");
  };

  const handleFolderClick = (folder: string) => {
    onFolderChange(folder);

    if (folder === "received") {
      setRequest((prev) => ({
        ...prev,
        spam: false,
        archived: false
        // NO modificar read - preservar el valor actual
      }));
    } else if (folder === "archived") {
      setRequest((prev) => ({
        ...prev,
        archived: true,
        spam: false
        // NO modificar read - preservar el valor actual
      }));
    } else if (folder === "spam") {
      setRequest((prev) => ({
        ...prev,
        spam: true,
        archived: false
        // NO modificar read - preservar el valor actual
      }));
    }
  };

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="d-flex" style={{ height: "100%" }}>
      {/* Barra lateral izquierda fija */}
      <div
        className="bg-light border-end d-flex flex-column align-items-center p-2"
        style={{
          width: "60px",
          minWidth: "60px",
          position: "sticky",
          top: 0,
          height: "calc(100vh - 56px)",
          zIndex: 9
        }}
      >
        <TooltipComponent
          title={
            isExpanded
              ? translate.CloseSidebar || "Cerrar barra lateral"
              : translate.OpenSidebar || "Abrir barra lateral"
          }
        >
          <button
            className="btn btn-link p-2 mb-3"
            onClick={toggleSidebar}
            style={{ color: "#0076b6" }}
          >
            <i
              className={`far ${isExpanded ? "fa-chevron-left" : "fa-chevron-right"}`}
            ></i>
          </button>
        </TooltipComponent>

        <TooltipComponent title={translate.Email || "Email"}>
          <button
            className={`btn btn-link p-3 mb-2 rounded-circle ${isEmailSelected ? "text-white" : "text-secondary"}`}
            onClick={handleEmailClick}
            style={{
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isEmailSelected ? "#0076b6" : "#e3f2fd"
            }}
          >
            <i className="far fa-envelope"></i>
          </button>
        </TooltipComponent>

        {CompanyService.canUseWhatsapp() && (
          <TooltipComponent title={translate.WhatsApp || "WhatsApp"}>
            <button
              className={`btn btn-link p-3 rounded-circle ${isWhatsAppSelected ? "text-white" : "text-secondary"}`}
              onClick={handleWhatsAppClick}
              style={{
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isWhatsAppSelected ? "#0076b6" : "#e3f2fd"
              }}
            >
              <i className="fa-brands fa-whatsapp"></i>
            </button>
          </TooltipComponent>
        )}
      </div>

      {/* Barra lateral expandible */}
      <div
        className="py-3 ps-3"
        style={{
          width: isExpanded ? "220px" : "0",
          minWidth: isExpanded ? "220px" : "0",
          maxWidth: isExpanded ? "220px" : "0",
          position: "sticky",
          top: 0,
          height: "auto",
          zIndex: 10,
          overflow: "hidden",
          opacity: isExpanded ? 1 : 0,
          pointerEvents: isExpanded ? "auto" : "none",
          transition:
            "width 0.3s ease-in-out, min-width 0.3s ease-in-out, max-width 0.3s ease-in-out, opacity 0.3s ease-in-out"
        }}
      >
        <div
          style={{
            overflowY: "auto",
            height: "auto",
            width: "210px"
          }}
        >
          {isEmailSelected ? (
            <div>
              <h6 className="text-muted text-uppercase small mb-3">
                {translate.Email || "Email"}
              </h6>
              <div className="list-group list-group-flush gap-2">
                <button
                  className="list-group-item list-group-item-action border-0 px-3 py-2 d-flex justify-content-between align-items-center rounded"
                  onClick={() => handleFolderClick("received")}
                  style={{
                    backgroundColor:
                      selectedFolder === "received" ? "#e3f2fd" : "#f0f8ff",
                    color: selectedFolder === "received" ? "#424242" : "#9e9e9e"
                  }}
                >
                  <span>
                    <i
                      className={`far fa-inbox me-2 ${selectedFolder === "received" ? "" : "text-muted"}`}
                      style={{
                        color:
                          selectedFolder === "received" ? "#424242" : "#9e9e9e"
                      }}
                    ></i>
                    {translate.Received || "Recibidos"}
                  </span>
                  {inboxCount?.countMail && inboxCount.countMail > 0 && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor:
                          selectedFolder === "received" ? "#424242" : "#9e9e9e",
                        color: "white"
                      }}
                    >
                      {inboxCount.countMail}
                    </span>
                  )}
                </button>
                <button
                  className="list-group-item list-group-item-action border-0 px-3 py-2 rounded"
                  onClick={() => handleFolderClick("archived")}
                  style={{
                    backgroundColor:
                      selectedFolder === "archived" ? "#e3f2fd" : "#f0f8ff",
                    color: selectedFolder === "archived" ? "#424242" : "#9e9e9e"
                  }}
                >
                  <i
                    className={`far fa-archive me-2 ${selectedFolder === "archived" ? "" : "text-muted"}`}
                    style={{
                      color:
                        selectedFolder === "archived" ? "#424242" : "#9e9e9e"
                    }}
                  ></i>
                  {translate.Archived || "Archivados"}
                </button>
                <button
                  className="list-group-item list-group-item-action border-0 px-3 py-2 rounded"
                  onClick={() => handleFolderClick("spam")}
                  style={{
                    backgroundColor:
                      selectedFolder === "spam" ? "#e3f2fd" : "#f0f8ff",
                    color: selectedFolder === "spam" ? "#424242" : "#9e9e9e"
                  }}
                >
                  <div
                    className={`inboxIcon me-2`}
                    style={{
                      WebkitMaskImage: `url(${spamIconSrc})`,
                      maskImage: `url(${spamIconSrc})`,
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                      WebkitMaskSize: "contain",
                      maskSize: "contain",
                      WebkitMaskPosition: "center",
                      maskPosition: "center",
                      backgroundColor:
                        selectedFolder === "spam" ? "#424242" : "#6c757d",
                      height: "17px",
                      width: "17px",
                      verticalAlign: "text-bottom"
                    }}
                  />
                  {translate.Spam || "Spam"}
                </button>
              </div>
            </div>
          ) : isWhatsAppSelected ? (
            <div>
              <h6 className="text-muted text-uppercase small mb-3">
                {translate.WhatsApp || "WhatsApp"}
              </h6>
              <div className="list-group list-group-flush gap-2">
                <button
                  className="list-group-item list-group-item-action border-0 px-3 py-2 d-flex justify-content-between align-items-center rounded"
                  onClick={() => handleFolderClick("received")}
                  style={{
                    backgroundColor:
                      selectedFolder === "received" ? "#e3f2fd" : "#f0f8ff",
                    color: selectedFolder === "received" ? "#424242" : "#9e9e9e"
                  }}
                >
                  <span>
                    <i
                      className={`far fa-inbox me-2 ${selectedFolder === "received" ? "" : "text-muted"}`}
                      style={{
                        color:
                          selectedFolder === "received" ? "#424242" : "#9e9e9e"
                      }}
                    ></i>
                    {translate.Received || "Recibidos"}
                  </span>
                  {inboxCount?.countWA && inboxCount.countWA > 0 && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor:
                          selectedFolder === "received" ? "#424242" : "#9e9e9e",
                        color: "white"
                      }}
                    >
                      {inboxCount.countWA}
                    </span>
                  )}
                </button>
                <button
                  className="list-group-item list-group-item-action border-0 px-3 py-2 rounded"
                  onClick={() => handleFolderClick("archived")}
                  style={{
                    backgroundColor:
                      selectedFolder === "archived" ? "#e3f2fd" : "#f0f8ff",
                    color: selectedFolder === "archived" ? "#424242" : "#9e9e9e"
                  }}
                >
                  <i
                    className={`far fa-archive me-2 ${selectedFolder === "archived" ? "" : "text-muted"}`}
                    style={{
                      color:
                        selectedFolder === "archived" ? "#424242" : "#9e9e9e"
                    }}
                  ></i>
                  {translate.Archived || "Archivados"}
                </button>
                {CompanyService.canMonitorAgents() &&
                  CompanyService.getSetting("allowwhatsappchatbot") && (
                    <button
                      className="list-group-item list-group-item-action border-0 px-3 py-2 rounded"
                      onClick={() => handleFolderClick("monitor")}
                      style={{
                        backgroundColor:
                          selectedFolder === "monitor" ? "#e3f2fd" : "#f0f8ff",
                        color:
                          selectedFolder === "monitor" ? "#424242" : "#9e9e9e"
                      }}
                    >
                      <i
                        className={`far fa-users me-2`}
                        style={{
                          color:
                            selectedFolder === "monitor" ? "#424242" : "#9e9e9e"
                        }}
                      ></i>
                      {translate.AgentMonitor || "Monitor de agentes"}
                    </button>
                  )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
